// Copyright © 2026 Qcells. All rights reserved.
//
// This software is proprietary and confidential. Unauthorized use,
// duplication, or distribution of software is strictly prohibited.

import type { ReactElement } from 'react';
import type { PowerSymbol } from '../../../../config/powerFlowSymbols';
import type { SymbolGlyphProps } from './glyph';
import { UtilitySymbol } from './UtilitySymbol';
import { GeneratorSymbol } from './GeneratorSymbol';
import { BatterySymbol } from './BatterySymbol';
import { UpsSymbol } from './UpsSymbol';
import { PduSymbol } from './PduSymbol';
import { MotorSymbol } from './MotorSymbol';
import { PumpSymbol } from './PumpSymbol';
import { FanSymbol } from './FanSymbol';
import { TransformerSymbol } from './TransformerSymbol';
import { MeterSymbol } from './MeterSymbol';
import { ChipSymbol } from './ChipSymbol';

export type SymbolGlyph = (_props: SymbolGlyphProps) => ReactElement;

/**
 * `PowerSymbol` → the glyph that draws it.
 *
 * **Partial on purpose.** Three of the sixteen symbols are not built —
 * `transfer-switch` and `rack` have no published instances and `protection` is
 * drawn on the conductor by `BreakerMark`, not as a device — so building them
 * would ship glyphs that render for zero boxes on every site we hold. They are
 * absent here rather than stubbed, and `glyphFor` resolves them to
 * `ChipSymbol`. `transformer` joined the table when GA-10 published nine.
 *
 * That is the whole mechanism: a deferred symbol is not a gap, it is the chip
 * the diagram already drew. Nothing branches on "unsupported", nothing renders
 * empty, and adding one later is a single line in this table.
 *
 * The same fallback catches an entity type the ontology gains between
 * re-syncs — the behaviour `UNMAPPED_SYMBOL` already chose in
 * `config/powerFlowSymbols.ts`. This extends that decision rather than
 * inventing a second one.
 *
 * See `docs/power-flow/power-flow-component-architecture.md` §2 (R1) for why,
 * and §5 for what each deferred symbol is waiting on.
 *
 * **Provenance.** Every glyph in this directory, and the breaker box in
 * `../conductors/BreakerMark.tsx`, is original artwork drawn on the shared
 * 24×24 grid following IEC 60617 / IEEE 315 one-line conventions. Nothing is
 * copied from Ignition's Symbol Factory or any other licensed library, and
 * nothing here needs a third-party licence recorded.
 */
export const SYMBOL_GLYPHS: Partial<Record<PowerSymbol, SymbolGlyph>> = {
  utility: UtilitySymbol,
  generator: GeneratorSymbol,
  battery: BatterySymbol,
  ups: UpsSymbol,
  pdu: PduSymbol,
  motor: MotorSymbol,
  pump: PumpSymbol,
  fan: FanSymbol,
  transformer: TransformerSymbol,
  meter: MeterSymbol,
  chip: ChipSymbol,
};

/**
 * Symbols drawn as a *bar* rather than a glyph.
 *
 * A switchboard is electrically a bar — everything tapped off it is at the same
 * potential — so `SldBus` renders these, and they never reach the glyph table.
 * Callers use this to decide which component to route to.
 */
export const BUS_SYMBOLS: ReadonlySet<PowerSymbol> = new Set<PowerSymbol>(['switchgear', 'busway']);

/** True when this symbol is drawn as a bus bar instead of a chip. */
export const isBusSymbol = (symbol: PowerSymbol): boolean => BUS_SYMBOLS.has(symbol);

/**
 * The glyph for this symbol. Never returns undefined — an unbuilt or unknown
 * symbol resolves to the chip, which is what the diagram drew before symbols
 * existed.
 */
export const glyphFor = (symbol: PowerSymbol): SymbolGlyph => SYMBOL_GLYPHS[symbol] ?? ChipSymbol;

/** True when this symbol has a glyph of its own rather than the fallback. */
export const hasGlyph = (symbol: PowerSymbol): boolean => SYMBOL_GLYPHS[symbol] !== undefined;

export {
  UtilitySymbol,
  GeneratorSymbol,
  BatterySymbol,
  UpsSymbol,
  PduSymbol,
  MotorSymbol,
  PumpSymbol,
  FanSymbol,
  TransformerSymbol,
  MeterSymbol,
  ChipSymbol,
};
export type { SymbolGlyphProps, RunState } from './glyph';
